export interface IMastercardTestProfile {
  username: string
  password: string
  // The Beforepay-side profile this one stands in for, where the testing guide identifies one.
  profile?: string
  // What the profile is seeded with.
  scenario: string
  // A reason to think twice before reaching for it, as recorded in the testing guide.
  note?: string
}

// Transcribed from the Mastercard Testing & Validation Guide. Mastercard's own sandbox fixtures --
// shared by everyone testing against the sandbox, holding no real customer's data, and worthless
// anywhere else. Every password matches its username, which is how Mastercard publishes them.
const profile = (
  username: string,
  scenario: string,
  extra: { profile?: string; note?: string } = {}
): IMastercardTestProfile => ({ username, password: username, scenario, ...extra })

export const MASTERCARD_TEST_PROFILES: IMastercardTestProfile[] = [
  profile(
    'profile_voi_005',
    'High salary: one strong weekly salary stream. No rent, dividends, or other income.',
    { profile: 'Weekly paycycle user' }
  ),
  profile(
    'profile_voi_006',
    'Medium salary with fortnightly frequency. Occasional cash deposits.',
    { profile: 'Fortnightly paycycle user' }
  ),
  profile(
    'profile_voi_007',
    'High salary with monthly frequency. Rental income and dividend income.',
    { note: 'Not usable — account number is more than 9 digits' }
  ),
  profile('profile_voi_008', 'Salary with monthly frequency. Gig side income.', {
    profile: 'Monthly pay cycle',
  }),
  profile('profile_voi_009', 'Joint account with two salaries from different employers.', {
    profile: 'Monthly salary account with 12 digit account number',
    note: 'Not useful for Beforepay',
  }),
  profile(
    'profile_voi_010',
    'Job switch scenario: one active salary, one salary that ended mid-year, and newer employer active for 2 months.',
    { profile: 'Fortnightly salary account' }
  ),
  profile('profile_voi_011', 'Unemployment benefits only, such as JobSeeker.', {
    profile: 'Benefits user',
  }),
  profile('profile_voi_012', 'Unemployment benefits with some low value, irregular casual work.', {
    profile: 'Benefits user',
  }),
  profile(
    'profile_voi_013',
    'Sole trader with irregular incoming payments or transfers for invoices, without a salary keyword.',
    { profile: 'Weekly paycycle user' }
  ),
  profile(
    'profile_voi_014',
    'Company owner with a low value salary stream and a high value dividend stream.',
    { profile: 'Monthly pay cycle / salary as PayType' }
  ),
  profile(
    'profile_voi_015',
    'Retirement income only: regular pension-like income from superannuation.',
    { profile: 'No paycycle (retiree profile)' }
  ),
  profile(
    'profile_voi_016',
    'Retirement income and dividends: regular pension-like income from superannuation and a dividend stream.',
    { profile: 'No paycycle' }
  ),
  profile(
    'profile_voi_017',
    'New job with salary only within the last 3 months. Two payments are present.',
    { profile: 'Weekly paycycle user' }
  ),
  profile(
    'profile_voi_018',
    'Salary drop mid-year from the same employer. Salary amount reduces halfway through the year to less than 900 per month.'
  ),
  profile(
    'profile_voi_019',
    'Seasonal worker with income clustered into a 4-month block that ends more than 1 month before the report date.',
    { profile: 'No salary account, seasonal worker / weekly paycycle' }
  ),
  profile(
    'profile_voi_020',
    'Salary moves from one account to another mid-year. Same employer; first half uses account A and second half uses account B.'
  ),
  profile(
    'profile_voi_021',
    'Salary dates shift around public holidays. Salary is present for the full year with irregular dates, but remains identifiable as a recurring salary stream.'
  ),
  profile(
    'profile_voi_022',
    'Employer name changes slightly mid-year while salary cadence remains the same.'
  ),
  profile('profile_voi_023', 'Salary income with a large volume of non-income transfers.', {
    profile: 'Monthly income with no error flags',
    note: 'Account number is more than 9 digits',
  }),
  profile('profile_voi_024', 'Very low transaction history with no credits present.', {
    profile: 'No paycycle profile',
  }),
  profile('profile_voi_025', 'Two sets of salary income across two different account IDs.'),
  profile('profile_voi_026', 'Self-employment income earned exclusively through gig work.'),
  profile(
    'profile_au_s04',
    'Monthly salary in the earlier history, followed by five complete months without pay.'
  ),
  profile(
    'profile_au_s08',
    'Average income below $300/day, high card balance, and substantial loan liability.'
  ),
  profile(
    'profile_au_s09',
    'Fortnightly Centrelink credits plus employment income; benefits equal about 45% of total income.'
  ),
  profile(
    'profile_au_s11',
    'Three recent salary credits paid directly into savings; no transaction account.'
  ),
  profile('profile_au_s13', 'Weekly income of about $700, equivalent to approximately $100/day.'),
  profile(
    'profile_au_s19',
    'Frequent card purchases, high utilisation, repayments, interest, and fees.'
  ),
  profile(
    'profile_au_s21',
    'Personal loan with approximately $75,000 original principal and fortnightly repayments.'
  ),
  profile(
    'profile_au_s22',
    'High-volume low-value spending with average transaction amount below $300.'
  ),
  profile(
    'profile_au_s24',
    'Masked BSB and account-number display values, while retaining synthetic underlying identifiers.'
  ),
  profile('profile_4110', 'Personal transaction, savings, loan, mortgage and credit card.'),
  profile('profile_4111', 'Personal transaction, savings, loan, mortgage and credit card.'),
  profile('profile_4112', 'Personal transaction, savings, loan, mortgage and credit card.'),
  profile('profile_4113', 'Personal transaction, savings, loan, mortgage and credit card.'),
  profile('profile_4114', 'Personal transaction, savings, loan, mortgage and credit card.'),
  profile('profile_4115', 'Personal transaction, savings, loan, mortgage and credit card.'),
]

// The mock banks these profiles sign in against. Searching "FinBank" in the institution list brings
// up all three; the guide walks through FinBank Aus OAuth.
export const FINBANK_INSTITUTIONS = [
  { name: 'FinBank Aus OAuth', id: '200003' },
  { name: 'FinBank Aus B OAuth', id: '2002119' },
  { name: 'FinBank Aus C OAuth', id: '2002120' },
]

export const MASTERCARD_TEST_DATA_URL =
  'https://developer.mastercard.com/open-finance-au/documentation/integration-and-testing/test-the-apis/#verification-of-income-test-profiles'

export const TESTING_GUIDE_URL = 'https://app.clickup.com/36600616/v/dc/12wyt8-95496/12wyt8-235276'
